import { Component, inject, OnInit, viewChild } from "@angular/core";
import { Router } from "@angular/router";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { ToastrService } from "ngx-toastr";

import { AdaaHelper } from "../../../core/utils";
import { ConfirmationModalComponent, DataTableComponent } from "../../../shared/components";
import { Constants } from "../../../shared/constants/constants";
import { AdaaBoolean } from "../../../shared/constants/enums";
import { CustomButton, TableButtonClicked } from "../../../shared/models";
import { EntitiesApiService, LanguageService, SystemLayoutService } from "../../../shared/services";

@Component({
  selector: "adaa-entities-list",
  imports: [DataTableComponent],
  templateUrl: "./entities-list.html",
  styleUrl: "./entities-list.scss",
})
export class EntitiesListComponent implements OnInit {
  private readonly _entitiesApiService = inject(EntitiesApiService);
  private readonly _languageService = inject(LanguageService);
  private readonly _toastrService = inject(ToastrService);
  private readonly _modalService = inject(NgbModal);
  private readonly _router = inject(Router);
  private readonly _systemLayoutService = inject(SystemLayoutService);

  adaaDataTable = viewChild.required<DataTableComponent>("adaaDataTable");
  Constants = Constants;

  customButtons: CustomButton[] = [
    {
      iconName: "adaa-icon-adaa-ok-Y",
      eventName: "enable",
      iconTitle: "common.form.label.active",
      onlyIf: (data: { enabled: AdaaBoolean }) => {
        return AdaaBoolean.Y === data?.enabled;
      },
    },
    {
      iconName: "adaa-icon-adaa-ok-N",
      eventName: "disable",
      iconTitle: "common.form.label.inactive",
      onlyIf: (data: { enabled: AdaaBoolean }) => {
        return AdaaBoolean.N === data?.enabled;
      },
    },
  ];

  public ngOnInit(): void {
    this._systemLayoutService.hasActiveEntityChanged$.subscribe(() => {
      !AdaaHelper.isPMOEntity() && this._router.navigateByUrl("/console");
    });
  }

  public addNew() {
    this._router.navigateByUrl("/console/entities/create");
  }

  public getAction(value: TableButtonClicked) {
    switch (value.event) {
      case "enable":
      case "disable":
        this._updateState(value);
        break;
      case "view":
        this._router.navigateByUrl(`/console/entities/${value.data.id}`);
        break;
      case "edit":
        this._router.navigateByUrl(`/console/entities/edit/${value.data.id}`);
        break;
      case "delete":
        this._deleteEntity(value.data.id);
        break;

      default:
        break;
    }
  }

  private _updateState(value: TableButtonClicked) {
    const data = value.data;
    data.enabled = value.event === "enable" ? "N" : "Y";
    this._entitiesApiService.updateState(data).subscribe({
      next: () => {
        this._toastrService.success(
          this._languageService.translate(
            "notification.success." + (value.event === "enable" ? "deactivate" : "activate")
          )
        );
        this.adaaDataTable().loadTableData();
      },
    });
  }

  private _deleteEntity(id: number) {
    const modal = this._modalService.open(ConfirmationModalComponent, {
      centered: true,
      size: "md",
      modalDialogClass: this._languageService.modalDirection(),
    });

    modal.componentInstance.header = "entity.create.delete_yes_no_title";
    modal.componentInstance.title = "entity.create.delete_yes_no_information";

    modal.result.then((e) => {
      if (e) {
        this._entitiesApiService.deleteEntity(id).subscribe({
          next: () => {
            this._toastrService.success(this._languageService.translate("notification.success.remove"));
            this.adaaDataTable().loadTableData();
          },
        });
      }
    });
  }
}
